import { cva, type VariantProps } from 'class-variance-authority';
import { Slot } from 'radix-ui';
import * as React from 'react';

import { cn } from '@/design/utils';

/**
 * Every control the operator presses, and one rule decides how each of them hovers.
 *
 * **No hover state here is an alpha, and that is a correctness rule rather than a preference.** A
 * translucent hover has no contrast ratio of its own — it has one per backdrop — so the same button
 * measures differently on a card and on the page, and a variant whose background is overridden per
 * theme leaves its FOREGROUND behind on a surface nobody measured. Both failure modes were in this
 * file. Measured in Chromium against the real token sheet, before: a primary button's hover was
 * 4.25:1 in the light theme against a 4.5 floor, a destructive one 4.34:1 in the dark theme, and in
 * the dark theme every outline button came out at 1.21:1 and every ghost button at 2.66:1, because
 * a dark-only background override replaced the fill and nothing replaced the ink that had been
 * chosen to sit on the old one.
 *
 * So each variant now hovers to an OPAQUE token whose ratio is fixed and published in
 * `design/tokens.css`: a solid fill moves six points of lightness away from the page, in the
 * direction that adds contrast against the ink it carries, and everything else moves to the one
 * quiet `--hover-surface` the whole product shares. The ink never changes on hover at all, which is
 * what removes the class of defect rather than one instance of it.
 *
 * **`secondary` became `selected`, which is what its one call site was using it for.** The
 * analytics panel's tab strip drew the chosen tab with it, so a name borrowed from the registry was
 * carrying this product's SELECTED state, and carrying it differently from the three other places
 * that have one. It is now the same tinted surface the shell sidebar, the administration navigation
 * and the conflict dialog use — 15.39:1 light and 12.10:1 dark against the ink on it — and it has no
 * hover at all, because pressing the tab you are already on does nothing and a control that promises
 * otherwise is lying quietly.
 */
const buttonVariants = cva(
  "inline-flex shrink-0 items-center justify-center gap-2 rounded-md text-sm font-medium whitespace-nowrap transition-all outline-none focus-visible:border-ring focus-visible:ring-[3px] focus-visible:ring-ring/50 disabled:pointer-events-none disabled:opacity-50 aria-invalid:border-destructive aria-invalid:ring-destructive/20 dark:aria-invalid:ring-destructive/40 [&_svg]:pointer-events-none [&_svg]:shrink-0 [&_svg:not([class*='size-'])]:size-4",
  {
    variants: {
      variant: {
        /*
         * `--color-primary` IS `--accent`, so the hover names the token it actually moves to rather
         * than minting a primary-flavoured alias of it. That is deliberate twice over. An alias
         * spelt with "primary" in front of "hover" would CONTAIN the primary background utility as
         * a substring, and two suites assert a direction button's class list does not carry that
         * utility — they pass today only because tailwind-merge happens to drop it. And Tailwind
         * scans this file as plain text, so naming a class that does not exist, even in a comment
         * like this one, fails the build with "produced no CSS rule". Both are easy to meet by
         * accident and neither is easy to read afterwards.
         */
        default: 'bg-primary text-primary-foreground hover:bg-accent-hover',
        /*
         * The same red, the same ink and the same hover as the gate's exit button, which is the one
         * the operator manual describes. This used to be a `text-white` fill over a dimmed red in
         * the dark theme — a second red button, disagreeing with the documented one.
         */
        destructive:
          'bg-danger text-danger-foreground hover:bg-danger-hover focus-visible:ring-destructive/20 dark:focus-visible:ring-destructive/40',
        outline: 'border bg-control shadow-xs hover:bg-hover-surface dark:border-input',
        selected: 'bg-accent-muted font-medium text-foreground',
        ghost: 'hover:bg-hover-surface',
        link: 'text-primary underline-offset-4 hover:underline',
      },
      size: {
        default: 'h-9 px-4 py-2 has-[>svg]:px-3',
        xs: "h-6 gap-1 rounded-md px-2 text-xs has-[>svg]:px-1.5 [&_svg:not([class*='size-'])]:size-3",
        sm: 'h-8 gap-1.5 rounded-md px-3 has-[>svg]:px-2.5',
        lg: 'h-10 rounded-md px-6 has-[>svg]:px-4',
        icon: 'size-9',
        'icon-xs': "size-6 rounded-md [&_svg:not([class*='size-'])]:size-3",
        'icon-sm': 'size-8',
        'icon-lg': 'size-10',
      },
    },
    defaultVariants: {
      variant: 'default',
      size: 'default',
    },
  },
);

function Button({
  className,
  variant = 'default',
  size = 'default',
  asChild = false,
  ...props
}: React.ComponentProps<'button'> &
  VariantProps<typeof buttonVariants> & {
    asChild?: boolean;
  }) {
  const Comp = asChild ? Slot.Root : 'button';

  return (
    <Comp
      data-slot="button"
      data-variant={variant}
      data-size={size}
      className={cn(buttonVariants({ variant, size, className }))}
      {...props}
    />
  );
}

export { Button };
